import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionIcon from "@mui/icons-material/Description";
import { departmentService, readApiError } from "../../services/departmentService";

const BLUE = {
  50: "#e8f6f7",
  100: "#c2e6ea",
  200: "#c2e6ea",
  600: "#0c8a9b",
  700: "#0a7480",
  900: "#0f172a",
};
const fontFam = "'Plus Jakarta Sans', 'Inter', sans-serif";

// Picks a template out of the flat library and binds it to a level.
//
// Only templates with no level are offered. A template already sitting
// somewhere else could be moved here, but silently reparenting it from a picker
// that does not say where it currently lives is how a team loses a template it
// was still using — moving one is a deliberate act on its own level.
const AttachTemplateDialog = ({
  open,
  onClose,
  store_id,
  departmentId,
  node,
  breadcrumb = [],
  onAttached,
}) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (store_id === undefined) return;
    setLoading(true);
    setError("");
    try {
      const res = await departmentService.getUnassignedTemplates(store_id);
      const all = res?.data?.data || [];
      setTemplates(all.filter((tpl) => !tpl.dept_node_id));
    } catch (err) {
      setError(readApiError(err, "Could not load the template library."));
    } finally {
      setLoading(false);
    }
  }, [store_id]);

  useEffect(() => {
    if (open) {
      setSearch("");
      load();
    }
  }, [open, load]);

  const attach = async (tpl) => {
    setBusyId(tpl.id);
    setError("");
    try {
      await departmentService.assignTemplate(tpl.id, {
        department_id: departmentId,
        dept_node_id: node?.id || null,
      });
      onAttached();
      onClose();
    } catch (err) {
      setError(readApiError(err, "Could not attach that template."));
    } finally {
      setBusyId(null);
    }
  };

  const term = search.trim().toLowerCase();
  const filtered = term
    ? templates.filter(
        (tpl) =>
          tpl.template_name?.toLowerCase().includes(term) ||
          tpl.title?.toLowerCase().includes(term),
      )
    : templates;

  return (
    <Dialog
      open={open}
      onClose={busyId ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      slotProps={{ paper: { sx: { borderRadius: "14px" } } }}
    >
      <DialogTitle
        sx={{ fontFamily: fontFam, fontWeight: 700, color: BLUE[900], pb: 0.5 }}
      >
        Attach an existing template
        <Typography
          sx={{ fontFamily: fontFam, fontSize: 13, color: "#64748b", mt: 0.25 }}
        >
          to {breadcrumb.map((item) => item.name).join(" › ") || node?.name}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: "8px !important" }}>
        {error && (
          <Alert severity="error" sx={{ fontFamily: fontFam, mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search the library"
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
              </InputAdornment>
            ),
            sx: { fontFamily: fontFam, borderRadius: "10px" },
          }}
        />

        {loading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 4 }}>
            <CircularProgress size={22} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <DescriptionIcon sx={{ fontSize: 30, color: BLUE[200], mb: 1 }} />
            <Typography
              sx={{ fontFamily: fontFam, fontWeight: 700, color: BLUE[900] }}
            >
              {templates.length === 0
                ? "Every template already has a level"
                : "Nothing matches that search"}
            </Typography>
            <Typography
              sx={{
                fontFamily: fontFam,
                fontSize: 13.5,
                color: "#64748b",
                mt: 0.5,
              }}
            >
              {templates.length === 0
                ? "Create a new template on this level instead."
                : "Try a different name."}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {filtered.map((tpl) => (
              <Box
                key={tpl.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  px: 1.5,
                  py: 1.15,
                }}
              >
                <DescriptionIcon sx={{ fontSize: 18, color: "#a16207" }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: fontFam,
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#1e293b",
                    }}
                  >
                    {tpl.template_name}
                  </Typography>
                  <Typography
                    sx={{ fontFamily: fontFam, fontSize: 12, color: "#94a3b8" }}
                  >
                    {tpl.title} · {tpl.questions?.length ?? 0} question(s)
                  </Typography>
                </Box>
                <Chip
                  label={tpl.status}
                  size="small"
                  sx={{
                    fontFamily: fontFam,
                    fontWeight: 600,
                    fontSize: 10.5,
                    height: 20,
                    bgcolor: tpl.status === "ACTIVE" ? "#f0fdf4" : "#f1f5f9",
                    color: tpl.status === "ACTIVE" ? "#16a34a" : "#64748b",
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!!busyId}
                  onClick={() => attach(tpl)}
                  startIcon={
                    busyId === tpl.id ? <CircularProgress size={13} /> : null
                  }
                  sx={{
                    fontFamily: fontFam,
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: "8px",
                    whiteSpace: "nowrap",
                  }}
                >
                  Attach
                </Button>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={onClose}
          disabled={!!busyId}
          sx={{ fontFamily: fontFam, color: "#64748b", textTransform: "none" }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttachTemplateDialog;
